import Phaser from "phaser";
import { GAME_HEIGHT, GAME_WIDTH } from "../constants";
import { STORY_CONFIG, getStoryBiomeProfile, type StoryBiomeId } from "../story/storyConfig";
import type { WorldLifeLayers } from "./WorldLifeSystem";

export class SpiritPressureSystem {
  private overlay: Phaser.GameObjects.Graphics;
  private current = 0;
  private flash = 0;

  constructor(private scene: Phaser.Scene, layers: WorldLifeLayers) {
    this.overlay = scene.add.graphics().setScrollFactor(0);
    layers.fog.add(this.overlay);
  }

  reset() {
    this.current = 0;
    this.flash = 0;
    this.overlay.clear();
  }

  pulse(seconds: number = STORY_CONFIG.storyBeats.biomeFlashSeconds) {
    this.flash = Math.max(this.flash, seconds);
  }

  update(dt: number, rearThreat: number, danger: number, biome: StoryBiomeId, elapsed: number) {
    const profile = getStoryBiomeProfile(biome);
    const base = Phaser.Math.Clamp(rearThreat * 0.65 + danger * 0.5, 0, 1);
    const target = Phaser.Math.Clamp(base * profile.spiritMultiplier + this.flash * 0.16, 0, 1);
    this.current = Phaser.Math.Linear(this.current, target, target > this.current ? 0.12 : 0.045);
    this.flash = Math.max(0, this.flash - dt);
    this.draw(biome, elapsed);
    return this.current;
  }

  get pressure() {
    return this.current;
  }

  private draw(biome: StoryBiomeId, elapsed: number) {
    this.overlay.clear();
    if (!STORY_CONFIG.spiritPressure.enabled || this.current < 0.025) return;

    const cfg = STORY_CONFIG.spiritPressure;
    const opacity = Phaser.Math.Linear(cfg.minOpacity, cfg.maxOpacity, this.current);
    const isClear = biome === "bright_forest_breathing_area" || biome === "pack_call_exit";
    const color = isClear ? 0x162617 : 0x050807;
    const lowerAlpha = opacity * (isClear ? 0.42 : 1);
    const sideAlpha = opacity * (isClear ? 0.24 : 0.62);

    this.overlay.fillStyle(color, lowerAlpha);
    this.overlay.fillRect(0, GAME_HEIGHT - cfg.bottomHeight, GAME_WIDTH, cfg.bottomHeight);
    this.overlay.fillStyle(color, sideAlpha);
    this.overlay.fillRect(0, 0, cfg.edgeWidth, GAME_HEIGHT);
    this.overlay.fillRect(GAME_WIDTH - cfg.edgeWidth, 0, cfg.edgeWidth, GAME_HEIGHT);

    const eyeCount = Math.floor(cfg.maxEyeCount * Phaser.Math.Clamp((this.current - 0.18) / 0.82, 0, 1));
    for (let i = 0; i < eyeCount; i += 1) {
      const side = i % 2 === 0 ? 1 : -1;
      const x = side > 0 ? GAME_WIDTH - 28 - (i % 3) * 10 : 28 + (i % 3) * 10;
      const y = 420 + ((i * 137 + Math.floor(elapsed * 16)) % 650);
      const blink = 0.62 + Math.sin(elapsed * 2.6 + i * 1.7) * 0.28;
      const alpha = cfg.eyeAlpha * this.current * blink;
      this.overlay.fillStyle(0xb7ff00, alpha);
      this.overlay.fillEllipse(x, y, 10, 4);
      this.overlay.fillStyle(0x051000, alpha * 0.8);
      this.overlay.fillEllipse(x, y, 3, 3);
    }
  }
}
